export type XmlAttributes = Record<string, string>;

export function parseXmlAttributes(source: string): XmlAttributes {
  const attributes: XmlAttributes = {};
  const re = /([A-Za-z_:][\w:.-]*)\s*=\s*(["'])([\s\S]*?)\2/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(source)) !== null) {
    attributes[match[1]] = xmlUnescape(match[3]);
  }
  return attributes;
}

export function xmlUnescape(source: string): string {
  return source.replace(
    /&(?:lt|gt|quot|apos|amp|#\d+|#x[0-9a-f]+);/gi,
    (entity) => {
      switch (entity.toLowerCase()) {
        case '&lt;': return '<';
        case '&gt;': return '>';
        case '&quot;': return '"';
        case '&apos;': return "'";
        case '&amp;': return '&';
        default: {
          const hex = /^&#x([0-9a-f]+);$/i.exec(entity);
          const decimal = /^&#(\d+);$/.exec(entity);
          const value = hex ? parseInt(hex[1], 16) : decimal ? parseInt(decimal[1], 10) : NaN;
          if (!Number.isFinite(value) || value < 0 || value > 0x10ffff) return entity;
          try {
            return String.fromCodePoint(value);
          } catch {
            return entity;
          }
        }
      }
    },
  );
}

export function stripXmlTags(source: string): string {
  return xmlUnescape(source.replace(/<[^>]*>/g, ''));
}

export function normalizeZipPath(source: string): string | null {
  const normalized = source.replace(/\\/g, '/');
  const parts: string[] = [];
  for (const part of normalized.split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') {
      if (parts.length === 0) return null;
      parts.pop();
      continue;
    }
    parts.push(part);
  }
  return parts.join('/');
}

export function resolveZipTarget(basePartPath: string, target: string): string | null {
  if (!target) return null;
  if (target.startsWith('/')) return normalizeZipPath(target.slice(1));
  const slash = basePartPath.lastIndexOf('/');
  const baseDir = slash >= 0 ? basePartPath.slice(0, slash + 1) : '';
  return normalizeZipPath(`${baseDir}${target}`);
}

export function relationshipPartPath(partPath: string): string {
  const slash = partPath.lastIndexOf('/');
  const directory = slash >= 0 ? partPath.slice(0, slash) : '';
  const fileName = slash >= 0 ? partPath.slice(slash + 1) : partPath;
  return `${directory ? `${directory}/` : ''}_rels/${fileName}.rels`;
}

export interface ParsedRelationship {
  id: string;
  type: string;
  target: string;
  targetMode?: string;
}

export function parseRelationships(xml: string): Map<string, ParsedRelationship> {
  const relationships = new Map<string, ParsedRelationship>();
  const re = /<(?:\w+:)?Relationship\b([^>]*?)\/?\s*>/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(xml)) !== null) {
    const attrs = parseXmlAttributes(match[1]);
    const id = attrs.Id ?? attrs.id;
    const target = attrs.Target ?? attrs.target;
    if (!id || !target) continue;
    relationships.set(id, {
      id,
      type: attrs.Type ?? attrs.type ?? '',
      target,
      targetMode: attrs.TargetMode ?? attrs.targetMode,
    });
  }
  return relationships;
}

export function firstElementBody(xml: string, localName: string): string | null {
  const escaped = localName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = new RegExp(
    `<(?:\\w+:)?${escaped}\\b[^>]*>([\\s\\S]*?)<\\/(?:\\w+:)?${escaped}>`,
    'i',
  ).exec(xml);
  return match?.[1] ?? null;
}

export function firstElementText(xml: string, localName: string): string | null {
  const body = firstElementBody(xml, localName);
  return body == null ? null : stripXmlTags(body).trim();
}
